import { ContentItem } from "../../constants/contentItems";
import { memo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import BackgroundImage from "../../../../../../components/ui/BackgroundImage";
import CardButton from "./CardButton";
import CardHeading from "./CardHeading";

export interface ContentCardProps {
  item: ContentItem;
  onClick: () => void;
  className?: string;
}

// ─── Large Card (JOB-URI style) ─────────────

const LargeCard: React.FC<ContentCardProps> = ({
  item,
  onClick,
  className,
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    // Icon parallax (mouseX/Y → useSpring → iconX/Y/rotate) removed — per-frame
    // spring recalcs on every mousemove saturate the JS thread on bad hardware.
    // Scale + brightness hover kept — they're GPU-composited.
    <motion.div
      whileHover={
        shouldReduceMotion
          ? undefined
          : { scale: 1.012, filter: "brightness(1.08)" }
      }
      whileTap={shouldReduceMotion ? undefined : { scale: 0.985 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      style={{ willChange: "transform", filter: "brightness(1)" }}
    >
      <BackgroundImage
        imageSrc={item.backgroundImageSrc + ""}
        imageClassName={`${item.objectFitCover ? "!object-cover" : "!object-fill"}`}
      >
        <div
          className={[
            "overflow-hidden w-full h-full p-8 3440px:p-14",
            "min-h-[180px]",
            "chakra-petch-font",
            className,
          ].join(" ")}
        >
          {/** Content */}
          <div className="w-full h-full relative">
            <div>
              <CardHeading
                label={item.label}
                description={item.description}
                ctaText={item.ctaText}
                onCtaClick={onClick}
              />
            </div>

            {/** Floating icon — static (parallax removed for performance) */}
            <img
              src={item.iconSrc}
              alt="icon"
              draggable={false}
              width={300}
              height={200}
              className="absolute bottom-[-50px] 1600px:bottom-[unset] 1600px:top-0 right-0 w-full max-w-[180px] 1600px:max-w-[300px]"
            />

            {/** Hover shimmer sweep */}
            {!shouldReduceMotion && (
              <motion.div
                aria-hidden
                className="absolute inset-0 pointer-events-none overflow-hidden"
                initial={false}
              >
                <motion.div
                  className="absolute inset-y-0 w-[40%] bg-gradient-to-r from-transparent via-white/[0.06] to-transparent -skew-x-[18deg]"
                  initial={{ x: "-120%" }}
                  whileHover={{
                    x: "280%",
                    transition: { duration: 0.7, ease: "easeInOut" },
                  }}
                />
              </motion.div>
            )}
          </div>
        </div>
      </BackgroundImage>
    </motion.div>
  );
};

export default memo(LargeCard);
